import { ICurrency } from '../../../libraries/currencies';
import { ReadValues, Transaction } from '../../../libraries/transactions';

export interface TransactionDenom {
  cid: number;
  currency: ICurrency;
  cindex: number;
  value: number;
  received: number;
  dispensed: number;
}

export interface CurrTableColumn {
  cindex: number;
  quantity: number;
  value: number;
  amount: number;
}

export interface TransactionCurrencyInfo {
  currency: ICurrency;
  totalQty: number;
  totalAmount: number;
  denominations: TransactionDenom[]; // one entry per used denom in this block
  columns: CurrTableColumn[];
  received?: boolean;
  dispensed?: boolean;
  values?: ReadValues[];
}

export const processTransactionData = (
  transactionData: Transaction,
  currencies: ICurrency[]
): TransactionCurrencyInfo[] => {
  let result: TransactionCurrencyInfo[] = [];

  // Ensure MC entries have cid
  transactionData?.mc?.forEach(mc => {
    if (!mc.cid) mc.cid = transactionData.cid;
  });

  currencies.forEach(currency => {
    let mcForCurrency = transactionData.mc?.filter(mc => mc.cid === currency.cid) || [];

    if (mcForCurrency.length === 0) return;

    let receivedDenoms: TransactionDenom[] = [];
    let dispensedDenoms: TransactionDenom[] = [];

    mcForCurrency.forEach(mc => {
      let denom = currency.denominations.find(d => d.index === mc.index);
      if (!denom) return;

      let originType = Math.floor(mc.origin / 10);
      let destType = Math.floor(mc.destiny / 10);

      let isReceived = mc.origin === 0 || originType === 1 || originType === 2 || mc.origin === mc.destiny;
      let isDispensed = mc.destiny === 0 || (originType === 4 && destType === 7);

      // Optional: internal movement detection
      let isInternal = mc.origin === mc.destiny;

      if (isReceived || isInternal) {
        receivedDenoms.push({
          cid: currency.cid,
          currency,
          cindex: denom.index,
          value: denom.value,
          received: mc.quantity ?? 0,
          dispensed: 0
        });
      }

      if (isDispensed) {
        dispensedDenoms.push({
          cid: currency.cid,
          currency,
          cindex: denom.index,
          value: denom.value,
          received: 0,
          dispensed: mc.quantity ?? 0
        });
      }
    });

    if (receivedDenoms.length > 0) {
      result.push({
        currency,
        totalQty: receivedDenoms.reduce((s, d) => s + d.received, 0),
        totalAmount: receivedDenoms.reduce((s, d) => s + d.received * d.value * currency.factor, 0),
        denominations: receivedDenoms,
        columns: [],
        received: true,
        dispensed: false
      });
    }

    if (dispensedDenoms.length > 0) {
      result.push({
        currency,
        totalQty: dispensedDenoms.reduce((s, d) => s + d.dispensed, 0),
        totalAmount: dispensedDenoms.reduce((s, d) => s + d.dispensed * d.value * currency.factor, 0),
        denominations: dispensedDenoms,
        columns: [],
        received: false,
        dispensed: true
      });
    }
  });

  return result;
};
